import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { assetUrl } from '../config'
import {
  LayoutDashboard,
  MessagesSquare,
  Inbox,
  BookOpen,
  Palette,
  BarChart3,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import NotificationDropdown from './NotificationDropdown'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'

interface LayoutProps {
  children: React.ReactNode
  isConnected?: boolean
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Conversations', href: '/conversations', icon: MessagesSquare },
  { name: 'Inquiries', href: '/inquiries', icon: Inbox },
  { name: 'Knowledge', href: '/knowledge', icon: BookOpen },
  { name: 'Branding', href: '/branding', icon: Palette },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Settings', href: '/settings', icon: SettingsIcon },
]

/**
 * Live-connection indicator.
 *
 * This console's whole premise is that it reflects what the bot is doing right
 * now, so whether the socket is actually connected is real information, not
 * chrome — when it drops, every count on screen is quietly stale. The dot
 * animates only while connected, which makes "not live" read as a state rather
 * than as something still loading.
 */
const ConnectionStatus: React.FC<{ isConnected: boolean }> = ({ isConnected }) => (
  <div
    className="flex items-center gap-2"
    title={isConnected ? 'Receiving live updates' : 'Not receiving live updates — figures may be stale'}
  >
    <span className="relative flex h-2 w-2">
      {isConnected && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-70" />
      )}
      <span
        className={cn(
          'relative inline-flex h-2 w-2 rounded-full',
          isConnected ? 'bg-success' : 'bg-muted-foreground/40'
        )}
      />
    </span>
    <span className="hidden text-xs font-medium text-muted-foreground sm:inline">
      {isConnected ? 'Live' : 'Not live'}
    </span>
  </div>
)

const NavLinks: React.FC<{ pathname: string; onNavigate?: () => void }> = ({
  pathname,
  onNavigate,
}) => (
  <nav className="flex-1 space-y-1 px-3">
    {navigation.map((item) => {
      const isActive = pathname === item.href || pathname.startsWith(item.href + '/')
      return (
        <Link
          key={item.name}
          to={item.href}
          onClick={onNavigate}
          aria-current={isActive ? 'page' : undefined}
          className={cn(
            'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            isActive
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          )}
        >
          <item.icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-primary' : '')} />
          {item.name}
        </Link>
      )
    })}
  </nav>
)

const Layout: React.FC<LayoutProps> = ({ children, isConnected = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    toast.success('Signed out')
    navigate('/login')
  }

  const initials = user?.name?.charAt(0).toUpperCase() ?? '?'

  // The real lockup, not an icon-plus-label stand-in. Width is what is
  // constrained rather than height: the horizontal logo is 548x126, so fixing
  // the height would make it overflow a 256px sidebar. h-auto keeps the ratio.
  const brand = (
    <div className="px-6 py-5">
      <Link to="/" className="block" aria-label="VirtueNetz AI — go to dashboard">
        <img
          src="/logo-horizontal.svg"
          alt="VirtueNetz AI"
          width={548}
          height={126}
          className="h-auto w-[150px]"
        />
      </Link>
    </div>
  )

  const userPanel = (
    <div className="border-t p-3">
      <div className="flex items-center gap-3 rounded-md px-2 py-1.5">
        <Avatar className="h-8 w-8">
          {user?.profileImage && <AvatarImage src={assetUrl(user.profileImage)} alt="" />}
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{user?.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          aria-label="Sign out"
          title="Sign out"
          className="h-8 w-8 text-muted-foreground"
        >
          <LogOut />
        </Button>
      </div>
    </div>
  )

  return (
    <div className="flex h-screen overflow-hidden bg-muted/40">
      {/* Mobile drawer */}
      <div
        className={cn('fixed inset-0 z-40 md:hidden', !sidebarOpen && 'pointer-events-none')}
        aria-hidden={!sidebarOpen}
      >
        <div
          className={cn(
            'fixed inset-0 bg-foreground/40 transition-opacity duration-200',
            sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={cn(
            'relative flex h-full w-64 flex-col bg-background shadow-xl transition-transform duration-200 ease-in-out',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <div className="flex items-center justify-between pr-3">
            {brand}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
            >
              <X />
            </Button>
          </div>
          <Separator />
          <div className="flex-1 overflow-y-auto py-3">
            <NavLinks pathname={location.pathname} onNavigate={() => setSidebarOpen(false)} />
          </div>
          {userPanel}
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r bg-background md:flex">
        {brand}
        <Separator />
        <div className="flex-1 overflow-y-auto py-3">
          <NavLinks pathname={location.pathname} />
        </div>
        {userPanel}
      </aside>

      {/* Main column */}
      <div className="flex w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b bg-background px-4">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </Button>

          <h1 className="flex-1 truncate text-sm font-semibold capitalize">
            {location.pathname.split('/')[1] || 'Dashboard'}
          </h1>

          <div className="flex items-center gap-3">
            <ConnectionStatus isConnected={isConnected} />
            <Separator orientation="vertical" className="h-5" />
            <NotificationDropdown />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto focus:outline-none">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  )
}

export default Layout
